import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import WebcamCapture from "../pages/WebcamCapture"; // Import the WebcamCapture component

/*
  Filename:    CompleteRegistration.js
  Description: This page completes the registration - Step 2 of 2.
*/

const CompleteRegistration = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const registrationData = localStorage.getItem("registrationData");
    if (!registrationData) {
      navigate("/register");
      return;
    }
    window.scrollTo(0, 0);
  }, [navigate]);

  const url = process.env.REACT_APP_BACKURL + "/api/foodPantry/client";
  const signinUrl = process.env.REACT_APP_BACKURL + "/api/foodPantry/user";
  const [useWebcam, setUseWebcam] = useState(false);
  const [showWebcamModal, setShowWebcamModal] = useState(false);

  const [formData, setFormData] = useState({ photo: null });
  const [clientInfo, setClientInfo] = useState({ photo: "" });
  const [phoneInfo, setPhoneInfo] = useState({
    phoneID: "",
    countryCode: "",
    areaCode: "",
    telPrefix: "",
    lineNum: "",
    isActive: true,
    isSafe: true,
    phoneType: "",
  });
  const [addressInfo, setAddressInfo] = useState({
    addressID: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    zip: "",
    addressType: "",
  });

  const [errors, setErrors] = useState([]);

  const validateInputs = () => {
    const add1Reg = /^[A-Za-z0-9\s,.-]+$/;
    const add2Reg = /^[A-Za-z0-9\s,.-]*$/;
    const cityReg = /^[A-Za-z\s'-]+$/;
    const zipReg = /^\d{5}(-\d{4})?$/;
    const numReg = /^\d+$/;
    let newErrors = [];

    if (!add1Reg.test(addressInfo.addressLine1)) {
      newErrors.push(
        "Address Line 1 must only contain letters, numbers, spaces, commas, periods, or hyphens."
      );
    }
    if (addressInfo.addressLine2 && !add2Reg.test(addressInfo.addressLine2)) {
      newErrors.push(
        "Address Line 2 must only contain letters, numbers, spaces, commas, periods, or hyphens."
      );
    }
    if (!cityReg.test(addressInfo.city)) {
      newErrors.push("City must only contain letters, spaces, apostrophes, or hyphens.");
    }
    if (addressInfo.state === "default") {
      newErrors.push("Please select a state.");
    }
    if (!zipReg.test(addressInfo.zip)) {
      newErrors.push("Zip code must be in the format 11111 or 11111-1111.");
    }
    if (addressInfo.addressType === "default") {
      newErrors.push("Please select an address type.");
    }

    if (!numReg.test(Number(phoneInfo.countryCode))) {
      newErrors.push("Country code must be a number.");
    }
    if (!numReg.test(Number(phoneInfo.areaCode))) {
      newErrors.push("Area code must be a number.");
    }
    if (!numReg.test(Number(phoneInfo.telPrefix))) {
      newErrors.push("Telephone prefix must be a number.");
    }
    if (!numReg.test(Number(phoneInfo.lineNum))) {
      newErrors.push("Line number must be a number.");
    }
    if (phoneInfo.phoneType === "default") {
      newErrors.push("Please select a phone type.");
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleChange = (event) => {
    const { dataset, name, value, type, files } = event.target;

    if (type === "file") {
      const file = files[0];
      setClientInfo({ ...clientInfo, [name]: file });
      return;
    }

    if (dataset.type === "client") {
      setClientInfo({ ...clientInfo, [name]: value });
    }
    if (dataset.type === "phone") {
      if (name === "phoneNumber") {
        // Remove all non-digit characters
        const digits = value.replace(/\D/g, "").slice(0, 10);
        let formatted = "";
        if (digits.length === 0) {
          formatted = "";
        } else if (digits.length < 4) {
          // Only show digits as-is for less than 4 digits (no formatting)
          formatted = digits;
        } else if (digits.length < 7) {
          // Area code complete, prefix incomplete: (XXX) YYY
          formatted = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
        } else {
          // Full format: (XXX) YYY-ZZZZ
          formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
        }
        // Parse out areaCode, telPrefix, lineNum
        const areaCode = digits.slice(0, 3);
        const telPrefix = digits.slice(3, 6);
        const lineNum = digits.slice(6, 10);
        setPhoneInfo({ ...phoneInfo, areaCode, telPrefix, lineNum, formatted });
      } else {
        setPhoneInfo({ ...phoneInfo, [name]: value });
      }
    }
    if (dataset.type === "address") {
      setAddressInfo({ ...addressInfo, [name]: value });
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setFormData({ ...formData, photo: file });
    setClientInfo({ ...clientInfo, photo: file });
  };

  const handleCapture = (imgSrc) => {
    setClientInfo({ ...clientInfo, photo: imgSrc });
    setShowWebcamModal(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateInputs()) return;

    const registrationData = JSON.parse(localStorage.getItem("registrationData") || "{}");
    const params = {
      user: registrationData.user,
      client: { ...registrationData.client, ...clientInfo },
      phone: phoneInfo,
      address: addressInfo,
    };

    const signinParams = {
      userName: registrationData.user.userName,
      password: registrationData.user.password
    };

    try {
      // Submit registration data
      const registrationResponse = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params)
      });
      console.log("Registration response:", registrationResponse);
      if (!registrationResponse.ok) {
        let errorMsg = "Error completing registration.";
        try {
          const data = await registrationResponse.json();
          errorMsg = data.message || errorMsg;
        } catch (e) { }
        throw new Error(errorMsg);
      }

      console.log("signing paramss", signinParams);
      // Sign in user
      const signinResponse = await fetch(signinUrl, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(signinParams)
      });
      if (!signinResponse.ok) {
        let errorMsg = "Error signing in after registration.";
        try {
          const data = await signinResponse.json();
          errorMsg = data.message || errorMsg;
        } catch (e) { }
        throw new Error(errorMsg);
      }

      localStorage.removeItem("registrationData");
      navigate("/dashboard");
    } catch (error) {
      setErrors([
        error.response?.data?.message || "Error completing registration.",
      ]);
      alert("Registration completion failed");
    }
  };

  return (
    <React.Fragment>
      <div className="col-12 d-flex justify-content-center align-items-center min-vh-100">
        <div className="card col-sm-12 col-md-10 col-lg-8">
          <div className="card-body">
            <div className="col-12 d-flex justify-content-center align-items-center">
              <img
                src="images/BridgingHopeWord.svg"
                className="img-fluid col-6"
                alt="logo"
              />
            </div>

            {/* Progress indicator */}
            <div className="mt-4 mb-4 d-flex justify-content-between align-items-center">
              <div className="text-center">
                <div
                  className="rounded-circle bg-success text-white d-inline-flex align-items-center justify-content-center"
                  style={{ width: 40, height: 40 }}
                >
                  ✓
                </div>
                <small className="d-block mt-2">Basic Info</small>
              </div>
              <div className="flex-grow-1 mx-3">
                <hr className="border-2 border-success" />
              </div>
              <div className="text-center">
                <div
                  className="rounded-circle bg-primary text-white d-inline-flex align-items-center justify-content-center"
                  style={{ width: 40, height: 40 }}
                >
                  2
                </div>
                <small className="d-block mt-2">Complete Profile</small>
              </div>
            </div>

            <div className="text-center mb-4">
              <h2>Complete Your Profile</h2>
              <p className="text-muted">
                Add your contact information, address, and photo to finish setting up your account.
              </p>
            </div>

            <form className="mt-3" onSubmit={handleSubmit}>
              {/* Profile Photo & Contact Information on same line */}
              <div className="row g-2 m-2">
                {/* Profile Photo */}
                <div className="col-12 col-lg-6">
                  <div className="card border border-light h-100">
                    <div className="card-header border-bottom border-light">
                      <h3 className="text-center mb-1">Profile Photo (Optional)</h3>
                    </div>
                    <div className="card-body">
                      <label className="mb-3">Upload a file or use webcam:</label>
                      <div className="d-flex mb-3">
                        <div className="me-3">
                          <input
                            type="radio"
                            id="uploadFile"
                            name="uploadOption"
                            value="file"
                            checked={!useWebcam}
                            onChange={() => setUseWebcam(false)}
                          />
                          <label htmlFor="uploadFile" className="ms-2">
                            Upload File
                          </label>
                        </div>
                        <div>
                          <input
                            type="radio"
                            id="useWebcam"
                            name="uploadOption"
                            value="webcam"
                            checked={useWebcam}
                            onChange={() => setUseWebcam(true)}
                          />
                          <label htmlFor="useWebcam" className="ms-2">
                            Use Webcam
                          </label>
                        </div>
                      </div>

                      {!useWebcam && (
                        <input
                          type="file"
                          name="photo"
                          className="form-control"
                          onChange={handleFileChange}
                        />
                      )}
                      {useWebcam && (
                        <div className="d-flex justify-content-center">
                          <button
                            type="button"
                            className="btn btn-primary mt-3"
                            onClick={() => setShowWebcamModal(true)}
                          >
                            Open Webcam
                          </button>
                        </div>
                      )}

                      {clientInfo.photo && (
                        <div className="mt-3">
                          {typeof clientInfo.photo === "string" ? (
                            <img
                              src={clientInfo.photo}
                              alt="Captured"
                              className="img-fluid"
                              style={{ width: 320, border: "2px solid black" }}
                            />
                          ) : (
                            <p>Uploaded File: {clientInfo.photo.name}</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {/* Contact Information */}
                <div className="col-12 col-lg-6">
                  <div className="card border border-light h-100">
                    <div className="card-header border-bottom border-light">
                      <h3 className="text-center mb-1">Contact Information</h3>
                    </div>
                    <div className="card-body row g-2">
                      <div className="col-12">
                        <div className="row g-0 align-items-center">
                          <div className="col-4">
                            <select
                              id="numCountryCode"
                              className="form-select"
                              data-type="phone"
                              name="countryCode"
                              value={phoneInfo.countryCode}
                              onChange={handleChange}
                              required
                            >
                              <option value="" disabled hidden>Country Code</option>
                              <option value="1">+1</option>
                            </select>
                          </div>
                          <div className="col-8">
                            <input
                              id="telPhoneNumber"
                              type="tel"
                              className="form-control"
                              placeholder="(555) 555-5555"
                              data-type="phone"
                              name="phoneNumber"
                              value={phoneInfo.formatted || ""}
                              onChange={handleChange}
                              inputMode="numeric"
                              pattern="\(\d{3}\) \d{3}-\d{4}"
                              aria-label="Enter your phone number in format (555) 555-5555"
                              required
                            />
                          </div>
                        </div>
                      </div>
                      <div className="col-12">
                        <select
                          id="txtPhoneType"
                          className="form-select"
                          data-type="phone"
                          name="phoneType"
                          value={phoneInfo.phoneType}
                          onChange={handleChange}
                          required
                        >
                          <option value="" disabled hidden>Phone Type</option>
                          <option value="Personal">Personal</option>
                          <option value="Work">Work</option>
                          <option value="Business">Business</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Address Information */}
              <div className="card border border-light m-2">
                <div className="card-header border-bottom border-light">
                  <h3 className="text-center mb-1">Address Information</h3>
                </div>
                <div className="card-body">
                  <div className="row g-2">
                    <div className="col-12 col-lg-6">
                      <div className="form-floating">
                        <input
                          id="txtAddressLine1"
                          type="text"
                          className="form-control"
                          placeholder="1234 Example St."
                          aria-label="Enter your street address line 1 here"
                          data-type="address"
                          name="addressLine1"
                          value={addressInfo.addressLine1}
                          onChange={handleChange}
                          required
                        />
                        <label htmlFor="txtAddressLine1" className="form-label">
                          Address Line 1
                        </label>
                      </div>
                    </div>
                    <div className="col-12 col-lg-6">
                      <div className="form-floating">
                        <input
                          id="txtAddressLine2"
                          type="text"
                          className="form-control"
                          placeholder="APT A100"
                          aria-label="Enter your street address line 2 here (optional)"
                          data-type="address"
                          name="addressLine2"
                          value={addressInfo.addressLine2}
                          onChange={handleChange}
                        />
                        <label htmlFor="txtAddressLine2" className="form-label">
                          Address Line 2 (Optional)
                        </label>
                      </div>
                    </div>

                    {/* City, State, Zip, Type on one line */}
                    <div className="col-12">
                      <div className="row g-2">
                        <div className="col-12 col-md-3">
                          <div className="form-floating">
                            <input
                              id="txtCity"
                              type="text"
                              className="form-control"
                              placeholder="Example City"
                              aria-label="Enter your city here"
                              data-type="address"
                              name="city"
                              value={addressInfo.city}
                              onChange={handleChange}
                              required
                            />
                            <label htmlFor="txtCity" className="form-label">
                              City
                            </label>
                          </div>
                        </div>
                        <div className="col-12 col-md-3">
                          <select
                            id="txtState"
                            className="form-select h-100"
                            data-type="address"
                            name="state"
                            value={addressInfo.state}
                            onChange={handleChange}
                            required
                          >
                            <option value="" disabled hidden>State</option>
                            <option value="AL">Alabama</option>
                            <option value="AK">Alaska</option>
                            <option value="AZ">Arizona</option>
                            <option value="AR">Arkansas</option>
                            <option value="CA">California</option>
                            <option value="CO">Colorado</option>
                            <option value="CT">Connecticut</option>
                            <option value="DE">Delaware</option>
                            <option value="DC">District Of Columbia</option>
                            <option value="FL">Florida</option>
                            <option value="GA">Georgia</option>
                            <option value="HI">Hawaii</option>
                            <option value="ID">Idaho</option>
                            <option value="IL">Illinois</option>
                            <option value="IN">Indiana</option>
                            <option value="IA">Iowa</option>
                            <option value="KS">Kansas</option>
                            <option value="KY">Kentucky</option>
                            <option value="LA">Louisiana</option>
                            <option value="ME">Maine</option>
                            <option value="MD">Maryland</option>
                            <option value="MA">Massachusetts</option>
                            <option value="MI">Michigan</option>
                            <option value="MN">Minnesota</option>
                            <option value="MS">Mississippi</option>
                            <option value="MO">Missouri</option>
                            <option value="MT">Montana</option>
                            <option value="NE">Nebraska</option>
                            <option value="NV">Nevada</option>
                            <option value="NH">New Hampshire</option>
                            <option value="NJ">New Jersey</option>
                            <option value="NM">New Mexico</option>
                            <option value="NY">New York</option>
                            <option value="NC">North Carolina</option>
                            <option value="ND">North Dakota</option>
                            <option value="OH">Ohio</option>
                            <option value="OK">Oklahoma</option>
                            <option value="OR">Oregon</option>
                            <option value="PA">Pennsylvania</option>
                            <option value="RI">Rhode Island</option>
                            <option value="SC">South Carolina</option>
                            <option value="SD">South Dakota</option>
                            <option value="TN">Tennessee</option>
                            <option value="TX">Texas</option>
                            <option value="UT">Utah</option>
                            <option value="VT">Vermont</option>
                            <option value="VA">Virginia</option>
                            <option value="WA">Washington</option>
                            <option value="WV">West Virginia</option>
                            <option value="WI">Wisconsin</option>
                            <option value="WY">Wyoming</option>
                          </select>
                        </div>
                        <div className="col-12 col-md-3">
                          <div className="form-floating">
                            <input
                              id="numZip"
                              type="text"
                              className="form-control"
                              placeholder="11111 or 11111-1111"
                              aria-label="Enter your zip code here"
                              data-type="address"
                              name="zip"
                              value={addressInfo.zip}
                              onChange={handleChange}
                              required
                            />
                            <label htmlFor="numZip" className="form-label">
                              Zip Code
                            </label>
                          </div>
                        </div>
                        <div className="col-12 col-md-3">
                          <select
                            id="txtAddressType"
                            className="form-select h-100"
                            placeholder="Home"
                            aria-label="Select the type of your address that you have entered here"
                            data-type="address"
                            name="addressType"
                            value={addressInfo.addressType}
                            onChange={handleChange}
                            required
                          >
                            <option value="" disabled hidden>Address Type</option>
                            <option value="Home">Home</option>
                            <option value="Shelter">Shelter</option>
                            <option value="Business">Business</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>


              {errors.length > 0 && (
                <div className="alert alert-danger m-2">
                  <h5>Please fix the following errors:</h5>
                  <ul className="mb-0">
                    {errors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="row g-2 m-2">
                <div className="col-12 col-lg-12">
                  <button type="submit" className="btn btn-primary col-12">
                    Complete Registration
                  </button>
                </div>
              </div>
            </form >

            <div className="text-center mt-2">
              <Link to="/signin">Already have an account? Sign in here.</Link>
            </div>
          </div >
        </div >
      </div >

      <footer className="footer">
        <div className="footer-body d-flex justify-content-between mx-4 pb-3">
          <ul className="list-inline mb-0 p-0">
            <li className="list-inline-item">
              <a href="./dashboard/extra/privacy-policy.html">Privacy Policy</a>
            </li>
            <li className="list-inline-item">
              <a href="./dashboard/extra/terms-of-service.html">Terms of Use</a>
            </li>
          </ul>
          <div className="right-panel">
            ©{new Date().getFullYear()} Bridging Hope. All Rights Reserved.
          </div>
        </div>
      </footer>

      {
        showWebcamModal && (
          <WebcamCapture
            onCapture={handleCapture}
            onClose={() => setShowWebcamModal(false)}
          />
        )
      }
    </React.Fragment >
  );
};

export default CompleteRegistration;
